import { useState } from "react";

import type { InventoryFamily, ProductStatus } from "../api/types";
import { ErrorNotice } from "../components/ui";
import { FAMILY_LABELS } from "./labels";
import { useExportProducts } from "./useBulkProducts";

/**
 * The catalogue as a workbook, narrowed by the server.
 *
 * The filters are the ones the backend really supports, and every one of them
 * is applied there: a download that fetched everything and narrowed it here
 * would be a whole catalogue in a browser's memory to produce a file about
 * twelve products.
 *
 * Product data only. No customer, no order, no administrator and no storage
 * detail is in the file, and the export tests hold that.
 */
const STATUSES: { value: ProductStatus | ""; label: string }[] = [
  { value: "", label: "Any status" },
  { value: "published", label: "Published" },
  { value: "unpublished", label: "Unpublished" },
  { value: "draft", label: "Draft" },
];

export function ExportPanel() {
  const [family, setFamily] = useState<InventoryFamily | "">("");
  const [status, setStatus] = useState<ProductStatus | "">("");
  const [search, setSearch] = useState("");
  const download = useExportProducts();

  return (
    <section className="panel bulk__panel" aria-labelledby="bulk-export">
      <h2 id="bulk-export" className="panel__title">
        Export products
      </h2>
      <div className="panel__body stack">
        <p className="muted">
          One row per sellable variant, with the identifiers an import needs to
          recognise what it is looking at. Stock and low-stock thresholds are
          included so the workbook is useful — changing them there is refused.
        </p>

        <div className="row bulk__filters">
          <label className="field">
            <span className="field__label" id="bulk-export-family">
              Product family
            </span>
            <select
              aria-labelledby="bulk-export-family"
              value={family}
              onChange={(event) =>
                setFamily(event.target.value as InventoryFamily | "")
              }
            >
              <option value="">All families</option>
              {(Object.keys(FAMILY_LABELS) as InventoryFamily[]).map((key) => (
                <option key={key} value={key}>
                  {FAMILY_LABELS[key]}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label" id="bulk-export-status">
              Status
            </span>
            <select
              aria-labelledby="bulk-export-status"
              value={status}
              onChange={(event) =>
                setStatus(event.target.value as ProductStatus | "")
              }
            >
              {STATUSES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label" id="bulk-export-search">
              Search
            </span>
            <input
              type="search"
              aria-labelledby="bulk-export-search"
              placeholder="Product name, code or path"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>

        <div className="row">
          <button
            type="button"
            className="btn btn--primary"
            disabled={download.isPending}
            onClick={() =>
              download.mutate({
                family: family || undefined,
                status: status || undefined,
                search: search.trim() || undefined,
              })
            }
          >
            {download.isPending ? "Preparing…" : "Export XLSX"}
          </button>
          {download.isSuccess ? (
            <span className="muted" role="status" data-testid="export-report">
              {download.data.products ?? 0} product
              {download.data.products === 1 ? "" : "s"} ·{" "}
              {download.data.rows ?? 0} row
              {download.data.rows === 1 ? "" : "s"} downloaded as{" "}
              {download.data.filename}
            </span>
          ) : null}
        </div>

        {download.isError ? <ErrorNotice error={download.error} /> : null}
      </div>
    </section>
  );
}
